import { useEffect, useLayoutEffect, useMemo, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import {
  formatAppointmentResponseBody,
  isSuccessfulAppointmentResponse,
  readAppointmentResponseBody,
} from '../appointmentResponse';
import { AppointmentResponseErrorDialog } from '../components/AppointmentResponseErrorDialog';
import { API_BASE_URL } from '../constants';
import './CancellationPage.css';

interface CancellationAppointment {
  id: string;
  when: string;
  procedureName?: string;
  practitionerIds?: string[];
  previousPage?: string;
}

interface FullPractitioner {
  //id: string; // DEPRECATED, use userId
  userId: string;
  firstName?: string;
  familyName?: string;
  title?: string;
}

const storageKey = 'pendingCancellationAppointment';

const readStoredAppointment = (): CancellationAppointment | null => {
  try {
    const raw = localStorage.getItem(storageKey);
    if (!raw) {
      return null;
    }

    const parsed = JSON.parse(raw);
    return parsed && typeof parsed === 'object' ? (parsed as CancellationAppointment) : null;
  } catch {
    return null;
  }
};

const readStoredPreviousPage = () => localStorage.getItem('previousPage')?.trim() || '/appointments';

const formatNameInitials = (firstName?: string) =>
  String(firstName ?? '')
    .trim()
    .split(/\s+/)
    .filter(Boolean)
    .map((part) => `${part.charAt(0)}.`)
    .join(' ');

const formatPractitionerName = (practitioner?: FullPractitioner) => {
  if (!practitioner) {
    return 'Nicht angegeben';
  }

  const title = String(practitioner.title ?? '').trim();
  const initials = formatNameInitials(practitioner.firstName);
  const familyName = String(practitioner.familyName ?? '').trim();

  return [title, initials, familyName].filter(Boolean).join(' ').trim() || 'Nicht angegeben';
};

const formatProcedureName = (value?: string) => value?.trim() || 'Behandlung';

const formatDate = (when?: string) => {
  if (!when) {
    return '--';
  }

  const date = new Date(when);
  if (Number.isNaN(date.getTime())) {
    return when;
  }

  return date.toLocaleDateString('de-DE', {
    weekday: 'long',
    day: '2-digit',
    month: 'short',
    year: 'numeric',
  });
};

const formatTime = (when?: string) => {
  if (!when) {
    return '--';
  }

  const start = new Date(when);
  if (Number.isNaN(start.getTime())) {
    return when;
  }

  const end = new Date(start);
  end.setMinutes(end.getMinutes() + 45);

  const startText = start.toLocaleTimeString('de-DE', { hour: '2-digit', minute: '2-digit' });
  const endText = end.toLocaleTimeString('de-DE', { hour: '2-digit', minute: '2-digit' });
  return `${startText} - ${endText} Uhr`;
};

const cancellationReasons = [
  'Reminder schicken in einem Monat per Mail',
  'Reminder schicken in drei Monaten per Mail',
  'Ich wünsche keinen Kontakt mehr',
] as const;

const detailLabels = ['Behandlung', 'Behandler', 'Datum', 'Zeit'] as const;

export function CancellationPage() {
  const location = useLocation();
  const navigate = useNavigate();
  const appointmentFromState = (location.state as { appointment?: CancellationAppointment } | null)?.appointment;
  const appointment = useMemo(() => appointmentFromState ?? readStoredAppointment(), [appointmentFromState]);
  const [selectedReason, setSelectedReason] = useState<string>('');
  const [practitionerName, setPractitionerName] = useState('Nicht angegeben');
  const [submitting, setSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState('');
  const previousPage = appointment?.previousPage?.trim() || readStoredPreviousPage();

  useLayoutEffect(() => {
    window.scrollTo({ top: 0, left: 0, behavior: 'instant' as ScrollBehavior });
  }, []);

  useEffect(() => {
    if (!appointment) {
      return;
    }

    localStorage.setItem(storageKey, JSON.stringify(appointment));
  }, [appointment]);

  useEffect(() => {
    const practitionerId = appointment?.practitionerIds?.[0];
    const token = localStorage.getItem('token');

    if (!practitionerId || !token) {
      setPractitionerName('Nicht angegeben');
      return;
    }

    let isActive = true;

    const loadPractitioner = async () => {
      try {
        const response = await fetch(`${API_BASE_URL}/practitioners/full/${practitionerId}`, {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        });

        if (!response.ok) {
          throw new Error(`Failed to load practitioner: ${response.status}`);
        }

        const practitioner = (await response.json()) as FullPractitioner;
        if (isActive) {
          setPractitionerName(formatPractitionerName(practitioner));
        }
      } catch (error) {
        console.error('Failed to load practitioner details', error);
        if (isActive) {
          setPractitionerName('Nicht angegeben');
        }
      }
    };

    loadPractitioner();

    return () => {
      isActive = false;
    };
  }, [appointment]);

  const handleCancelAppointment = async () => {
    if (!appointment?.id || !selectedReason || submitting) {
      return;
    }

    const token = localStorage.getItem('token');
    setSubmitting(true);
    setSubmitError('');

    console.log("CancellationPage.delete:");

    try {

      // TODO: always delete Ergodent Appointment also:
      const response = await fetch(`${API_BASE_URL}/appointments/delete`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({
          id: appointment.id,
          mode: "ergodent:demo",
        }),
      });

      const responseBody = await readAppointmentResponseBody(response);
      const deleteSuccessful = isSuccessfulAppointmentResponse(response, responseBody);

      if (!deleteSuccessful) {
        setSubmitError(`Error: ${formatAppointmentResponseBody(responseBody)}`);
        return;
      }

      localStorage.removeItem(storageKey);
      navigate('/cancellation-done', { state: { appointment } });
    } catch (error) {
      console.error('Failed to cancel appointment', error);
      setSubmitError(`Error: ${error instanceof Error ? error.message : String(error)}`);
    } finally {
      setSubmitting(false);
    }

  };

  const handleKeepAppointment = () => {
    navigate(previousPage);
  };

  if (!appointment?.when) {
    return (
      <div className="cancellation-page">
        <div className="cancellation-page__card cancellation-page__empty">
          <h1 className="text-3xl font-semibold text-slate-900">Kein Termin gefunden</h1>
          <p className="mt-4 text-sm leading-6 text-slate-400">
            Die Daten für die Terminabsage sind aktuell nicht verfügbar.
          </p>
        </div>
      </div>
    );
  }

  const detailValues = [
    formatProcedureName(appointment.procedureName),
    practitionerName,
    formatDate(appointment.when),
    formatTime(appointment.when),
  ];

  return (
    <div className="cancellation-page">
      <AppointmentResponseErrorDialog message={submitError} onClose={() => setSubmitError('')} />
      <main className="cancellation-page__card" aria-labelledby="cancellation-title">
        <div className="cancellation-page__heading">
          <div className="cancellation-page__icon">
            <svg viewBox="0 0 24 24" fill="none" aria-hidden="true" focusable="false">
              <rect x="5.5" y="6.5" width="13" height="13" rx="1.5" stroke="currentColor" strokeWidth="1.8" />
              <path d="M8 4.5v4M16 4.5v4M5.5 10h13" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" />
              <path d="m10.25 12.75 3.5 3.5m0-3.5-3.5 3.5" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" />
            </svg>
          </div>
          <h1 id="cancellation-title">Termin absagen</h1>
          <p className="cancellation-page__description">
            Sind Sie sicher, dass Sie diesen Termin absagen möchten? Diese Aktion kann nicht rückgängig gemacht werden.
          </p>
        </div>

        <section className="cancellation-page__details" aria-labelledby="cancellation-details-title">
          <h2 id="cancellation-details-title">Termindetails</h2>

          <dl>
            {detailLabels.map((label, index) => (
              <div
                key={label}
                className="cancellation-page__detail"
              >
                <dt>{label}</dt>
                <dd>{detailValues[index]}</dd>
              </div>
            ))}
          </dl>
        </section>

        <div className="cancellation-page__reasons" role="radiogroup" aria-label="Kontaktwunsch">
          {cancellationReasons.map((reason) => {
            const isSelected = selectedReason === reason;

            return (
              <label key={reason} className="cancellation-page__reason">
                <button
                  type="button"
                  role="radio"
                  aria-label={reason}
                  aria-checked={isSelected}
                  onClick={() => setSelectedReason(reason)}
                  className="cancellation-page__checkbox"
                >
                  {isSelected ? (
                    <svg viewBox="0 0 16 16" fill="none" aria-hidden="true" focusable="false">
                      <path
                        d="m3.5 8 3 3 6-6"
                        stroke="currentColor"
                        strokeWidth="2"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                      />
                    </svg>
                  ) : null}
                </button>
                <span>{reason}</span>
              </label>
            );
          })}
        </div>

        <div className="cancellation-page__actions">
          <button
            type="button"
            onClick={handleCancelAppointment}
            disabled={!selectedReason || submitting}
            className="cancellation-page__cancel"
          >
            {submitting ? 'Termin wird abgesagt...' : 'Termin jetzt absagen'}
          </button>
          <button
            type="button"
            onClick={handleKeepAppointment}
            disabled={submitting}
            className="cancellation-page__keep"
          >
            Nein, Termin behalten
          </button>
        </div>

        <p className="cancellation-page__contact">
          Haben Sie Fragen? Kontaktieren Sie uns unter{' '}
          <span>052 624 46 25</span>.
        </p>
      </main>
    </div>
  );
}
